/**
 * DeviceManagementCard Component
 *
 * Displays list of all registered devices for the user.
 * Shows device name, last sync time, provider, and allows device removal.
 */

// 1. External dependencies
import React, { useEffect, useState } from 'react';
import { Smartphone, Trash2, Monitor, Edit2, Check, X } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Chip,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Input,
  Pagination
} from '@enfomentis/ui';
import type { UserSyncDevice } from '@/types/supabase/UserSyncDevice';
import type { SyncResult } from '@/types/sync/SyncExport';

export interface DeviceManagementCardProps {
  /** List of registered devices */
  devices: UserSyncDevice[];
  /** Current device ID */
  currentDeviceId: string;
  /** Callback to remove a device */
  onRemove: (deviceId: string) => Promise<void>;
  /** Callback to rename a device */
  onRename: (deviceId: string, newName: string) => Promise<void>;
  /** Last sync result (triggers refresh when updated) */
  lastSyncResult: SyncResult | null;
}

/**
 * Device Management Card
 *
 * Displays table of registered sync devices with removal actions.
 * Highlights current device and prevents its removal.
 * Automatically refreshes displayed timestamps when lastSyncResult updates.
 */
export const DeviceManagementCard: React.FC<DeviceManagementCardProps> = ({
  devices,
  currentDeviceId,
  onRemove,
  onRename,
  lastSyncResult
}) => {
  const [removingDeviceId, setRemovingDeviceId] = useState<string | null>(null);
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [renamingDeviceId, setRenamingDeviceId] = useState<string | null>(null);
  const [, setRefreshTrigger] = useState(0);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = React.useRef(false);

  // Sort devices to show current device first
  const sortedDevices = React.useMemo(() => {
    return [...devices].sort((a, b) => {
      const aIsCurrent = a.deviceId === currentDeviceId;
      const bIsCurrent = b.deviceId === currentDeviceId;
      if (aIsCurrent && !bIsCurrent) return -1;
      if (!aIsCurrent && bIsCurrent) return 1;
      return 0;
    });
  }, [devices, currentDeviceId]);

  // Force re-render when lastSyncResult changes (to update timestamps)
  useEffect(() => {
    if (lastSyncResult?.success) {
      setRefreshTrigger(prev => prev + 1);
    }
  }, [lastSyncResult]);

  // Force re-render every minute to update relative timestamps ("Just now" -> "1 min ago")
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  const handleRemove = async (deviceId: string): Promise<void> => {
    setRemovingDeviceId(deviceId);
    try {
      await onRemove(deviceId);
    } finally {
      setRemovingDeviceId(null);
    }
  };

  const startEditing = (device: UserSyncDevice): void => {
    setEditingDeviceId(device.id);
    setEditingName(device.deviceName);

    // Scroll the carousel into view to ensure input isn't covered by keyboard
    setTimeout(() => {
      if (carouselRef.current) {
        carouselRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const cancelEditing = (): void => {
    setEditingDeviceId(null);
    setEditingName('');
  };

  const handleRename = async (deviceId: string): Promise<void> => {
    if (!editingName.trim()) {
      cancelEditing();
      return;
    }

    setRenamingDeviceId(deviceId);
    try {
      await onRename(deviceId, editingName.trim());
      setEditingDeviceId(null);
      setEditingName('');
    } finally {
      setRenamingDeviceId(null);
    }
  };

  // Format last sync timestamp
  const formatLastSync = (timestamp: string | null): string => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString();
  };


  // Device card renderer for mobile carousel
  const renderDeviceCard = (device: UserSyncDevice) => {
    const isCurrentDevice = device.deviceId === currentDeviceId;
    const isRemoving = removingDeviceId === device.id;
    const isEditing = editingDeviceId === device.id;
    const isRenaming = renamingDeviceId === device.id;

    return (
      <Card key={device.id}
        shadow="none"
        radius="sm"
        className="border border-default-300"
      >
        <CardHeader>
          {/* Device Name / Edit */}
          <div className="flex items-center gap-2 min-w-0">
            {/* <Monitor className="w-5 h-5 text-text-secondary flex-shrink-0" /> */}
            {isEditing ? (
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Input
                  size="sm"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleRename(device.id);
                    } else if (e.key === 'Escape') {
                      cancelEditing();
                    }
                  }}
                  autoFocus
                  className="flex-1 min-w-0"
                  isDisabled={isRenaming}
                  classNames={{
                    input: "text-base"
                  }}
                />
                <Button
                  size="sm"
                  color="success"
                  variant="light"
                  isIconOnly
                  onPress={() => handleRename(device.id)}
                  isLoading={isRenaming}
                  isDisabled={isRenaming}
                  className="flex-shrink-0"
                >
                  <Check className="w-5 h-5" />
                </Button>
                <Button
                  size="sm"
                  color="default"
                  variant="light"
                  isIconOnly
                  onPress={cancelEditing}
                  isDisabled={isRenaming}
                  className="flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="font-medium text-lg break-words">{device.deviceName}</span>
                {isCurrentDevice && (
                  <Chip size="sm" color="primary" variant="flat" className="flex-shrink-0">
                    This Device
                  </Chip>
                )}
                <Button
                  size="sm"
                  variant="light"
                  isIconOnly
                  onPress={() => startEditing(device)}
                  className="ml-auto flex-shrink-0"
                >
                  <Edit2 className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody className="p-4 space-y-3">
          {/* Sync Timestamps */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Last Sync:</span>
              <span className="font-medium break-words text-right">{formatLastSync(device.lastSyncAt)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Last Export:</span>
              <span className="font-medium break-words text-right">{formatLastSync(device.lastExportedAt)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Last Import:</span>
              <span className="font-medium break-words text-right">{formatLastSync(device.lastImportedAt)}</span>
            </div>
          </div>

          {/* Remove Button */}
          {!isCurrentDevice && !isEditing && (
            <Button
              size="lg"
              color="danger"
              variant="light"
              startContent={<Trash2 className="w-4 h-4" />}
              onPress={() => handleRemove(device.id)}
              isLoading={isRemoving}
              isDisabled={isRemoving}
              fullWidth
            >
              Remove Device
            </Button>
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <Smartphone className="w-5 h-5 text-text-secondary" />
        <h3 className="text-lg font-semibold">Registered Devices</h3>
        <Chip size="sm" variant="flat" className="ml-auto">
          {devices.length} {devices.length === 1 ? 'device' : 'devices'}
        </Chip>
      </CardHeader>
      <CardBody className="p-4 sm:p-6">
        {devices.length === 0 ? (
          <div className="text-center py-8">
            <Monitor className="w-12 h-12 text-text-tertiary mx-auto mb-3" />
            <p className="text-text-secondary break-words">No devices registered</p>
            <p className="text-sm text-text-tertiary mt-1 break-words">
              Devices will appear here after they sync
            </p>
          </div>
        ) : (
          <>
            {/* Mobile: Horizontal scroll carousel - constrained within card */}
            <div className="md:hidden">
              <div
                ref={carouselRef}
                className="max-w-xs overflow-x-auto snap-x snap-mandatory pb-2"
                onScroll={(e) => {
                  // Skip scroll updates during programmatic scrolling (pagination clicks)
                  if (isProgrammaticScrollRef.current) {
                    return;
                  }

                  const scrollLeft = e.currentTarget.scrollLeft;
                  const cardWidth = e.currentTarget.scrollWidth / sortedDevices.length;
                  const index = Math.round(scrollLeft / cardWidth);
                  setCurrentCarouselIndex(index);
                }}
              >
                <div className="flex gap-4">
                  {sortedDevices.map((device) => (
                    <div key={device.id} className="snap-start flex-shrink-0 w-[95%]">
                      {renderDeviceCard(device)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination dots using HeroUI */}
              {sortedDevices.length > 1 && (
                <div className="flex justify-center mt-3">
                  <Pagination
                    total={sortedDevices.length}
                    page={currentCarouselIndex + 1}
                    onChange={(page) => {
                      const newIndex = page - 1;

                      // Set flag to suppress onScroll updates during programmatic scrolling
                      isProgrammaticScrollRef.current = true;
                      setCurrentCarouselIndex(newIndex);

                      // Scroll carousel to the selected card
                      if (carouselRef.current) {
                        const cardWidth = carouselRef.current.scrollWidth / sortedDevices.length;
                        carouselRef.current.scrollTo({
                          left: cardWidth * newIndex,
                          behavior: 'smooth'
                        });

                        // Clear flag after scroll animation completes (~300ms for smooth scroll)
                        setTimeout(() => {
                          isProgrammaticScrollRef.current = false;
                        }, 500);
                      }
                    }}
                    showControls={false}
                    size="sm"
                    color="primary"
                    isCompact
                    dotsJump={1}
                  />
                </div>
              )}
            </div>

            {/* Desktop: Table */}
            <div className="hidden md:block">
              <Table aria-label="Registered devices" removeWrapper>
                <TableHeader>
                  <TableColumn>DEVICE</TableColumn>
                  <TableColumn>SYNC ACTIVITY</TableColumn>
                  <TableColumn>ACTIONS</TableColumn>
                </TableHeader>
                <TableBody>
                  {devices.map((device) => {
                    const isCurrentDevice = device.deviceId === currentDeviceId;
                    const isRemoving = removingDeviceId === device.id;
                    const isEditing = editingDeviceId === device.id;
                    const isRenaming = renamingDeviceId === device.id;

                    return (
                      <TableRow key={device.id}>
                        <TableCell>
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <Input
                                size="lg"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleRename(device.id);
                                  } else if (e.key === 'Escape') {
                                    cancelEditing();
                                  }
                                }}
                                autoFocus
                                className="max-w-xs"
                                isDisabled={isRenaming}
                              />
                              <Button
                                size="lg"
                                color="success"
                                variant="light"
                                isIconOnly
                                onPress={() => handleRename(device.id)}
                                isLoading={isRenaming}
                                isDisabled={isRenaming}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="lg"
                                color="default"
                                variant="light"
                                isIconOnly
                                onPress={cancelEditing}
                                isDisabled={isRenaming}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{device.deviceName}</span>
                              {isCurrentDevice && (
                                <Chip size="sm" color="primary" variant="flat">
                                  This Device
                                </Chip>
                              )}
                              <Button
                                size="lg"
                                variant="light"
                                isIconOnly
                                onPress={() => startEditing(device)}
                                className="ml-1"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-text-tertiary w-14">Sync:</span>
                              <span className="text-text-secondary">{formatLastSync(device.lastSyncAt)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-text-tertiary w-14">Export:</span>
                              <span className="text-text-secondary">{formatLastSync(device.lastExportedAt)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-text-tertiary w-14">Import:</span>
                              <span className="text-text-secondary">{formatLastSync(device.lastImportedAt)}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {!isCurrentDevice && !isEditing && (
                            <Button
                              size="lg"
                              color="danger"
                              variant="light"
                              startContent={<Trash2 className="w-4 h-4" />}
                              onPress={() => handleRemove(device.id)}
                              isLoading={isRemoving}
                              isDisabled={isRemoving}
                            >
                              Remove
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Help Text */}
        <div className="hidden">
        <p className="text-xs text-text-tertiary mt-4 break-words">
          Each device you use to access your journal is registered here. Remove old or unused devices
          to keep your sync clean.
        </p>
        </div>
      </CardBody>
    </Card>
  );
};
